import { useCallback, useMemo } from 'react';
import create from 'zustand';
import { Set } from 'immutable';
import { ApolloError, Reference } from '@apollo/client';
import { ClipObject, ClipObjectPending } from '../../../../context/clips';
import { ClipsConnection } from './utils/constants';
import { ProjectClips_V2QueryVariables } from '../../operation.generated';

export type ClipsRefs = Omit<ClipsConnection, 'edges'> & {
  edges: Array<{
    node: Reference;
    // Making this argument optional to narrow the scope of changes
    // however - we may want to always require in the near future
    highlights?: ClipsConnection['edges'][0]['highlights'];
    cursor?: string | null;
  }>;
};

export type UpdatableClipProperties = Pick<ClipObject, 'isCreditableBy'>;

// using zustand as a persistence layer for shared state
type StateWithActions = {
  // null is used to denote initial state to avoid requerying
  search: string | null;
  setSearch: (search: string) => void;
  previouslySelectedClip: string | null;
  setPreviouslySelectedClip: (clipId: string) => void;
  selectedClipIds: Set<string>;
  setSelectedClipIds: (newList: Set<string>) => void;
  lastMovedClipDestinationProjectId: string;
  setLastMovedClipDestinationProjectId: (projectId: string) => void;
  lastCopiedClipDestinationProjectId: string;
  setLastCopiedClipDestinationProjectId: (projectId: string) => void;
  lastUsedCombineClipMsGapDuration: number;
  setLastUsedCombineClipMsGapDuration: (duration: number) => void;
};

export const useStore = create<StateWithActions>(set => ({
  search: null,
  setSearch: (search: string) => {
    set(() => ({ search }));
  },
  previouslySelectedClip: null,
  setPreviouslySelectedClip: (clipId: string) =>
    set(() => ({ previouslySelectedClip: clipId })),
  selectedClipIds: Set(),
  setSelectedClipIds: (newList: Set<string>) =>
    set(state => ({
      selectedClipIds: newList,
      previouslySelectedClip:
        newList.size === 0 ? null : state.previouslySelectedClip,
    })),
  lastMovedClipDestinationProjectId: '',
  setLastMovedClipDestinationProjectId: (projectId: string) =>
    set(() => ({ lastMovedClipDestinationProjectId: projectId })),
  lastCopiedClipDestinationProjectId: '',
  setLastCopiedClipDestinationProjectId: (projectId: string) =>
    set(() => ({ lastCopiedClipDestinationProjectId: projectId })),
  lastUsedCombineClipMsGapDuration: 0, // default is 0.8
  setLastUsedCombineClipMsGapDuration: (duration: number) =>
    set(() => ({ lastUsedCombineClipMsGapDuration: duration })),
}));

export type StudioClipsDataAndActions = [
  {
    loading: boolean;
    error: ApolloError | undefined;
    clips: (ClipObject | ClipObjectPending)[];
    hasNextPage: boolean;
    previouslySelectedClip: string | null;
    selectedClipIds: Set<string>;
    lastMovedClipDestinationProjectId: string;
    lastCopiedClipDestinationProjectId: string;
    lastUsedCombineClipMsGapDuration: number;
    variables: ProjectClips_V2QueryVariables | undefined;
  },
  {
    fetchMore: () => Promise<void>;
    deleteClip: (clip: ClipObject) => Promise<void>;
    deleteClips: (clips: ClipObject[], clipIds: string[]) => void;
    addPendingClip: (clip: ClipObjectPending) => void;
    renameClip: (clip: ClipObject, newName: string) => void;
    updatePendingClip: (
      clipId: string,
      incomingClipProps: Partial<ClipObject>
    ) => void;
    deletePendingClip: (clipId: string) => void;
    setPreviouslySelectedClip: (clipId: string) => void;
    setSelectedClipIds: (clipIds: Set<string>) => void;
    updateClips: <T extends keyof UpdatableClipProperties>(
      id: string | string[],
      property: T,
      value: UpdatableClipProperties[T]
    ) => void;
    updateClipOrdering: (inputs: {
      clip: ClipObject;
      newIndex: number;
      precedingClip?: ClipObject;
    }) => void;
    updateClipProject: (
      clip: ClipObject,
      projectId: string,
      index: number,
      sequence: string
    ) => void;
    setLastMovedClipDestinationProjectId: (projectId: string) => void;
    setLastCopiedClipDestinationProjectId: (projectId: string) => void;
    setLastUsedCombineClipMsGapDuration: (duration: number) => void;
  }
];

export const usePersistentClipStateAndActions = () => {
  const search = useStore(useCallback(state => state.search, []));
  const setSearch = useStore(useCallback(state => state.setSearch, []));
  const previouslySelectedClip = useStore(
    useCallback(state => state.previouslySelectedClip, [])
  );
  const selectedClipIds = useStore(
    useCallback(state => state.selectedClipIds, [])
  );
  const setPreviouslySelectedClip = useStore(
    useCallback(state => state.setPreviouslySelectedClip, [])
  );
  const setSelectedClipIds = useStore(
    useCallback(state => state.setSelectedClipIds, [])
  );

  const lastMovedClipDestinationProjectId = useStore(
    useCallback(state => state.lastMovedClipDestinationProjectId, [])
  );
  const setLastMovedClipDestinationProjectId = useStore(
    useCallback(state => state.setLastMovedClipDestinationProjectId, [])
  );
  const lastCopiedClipDestinationProjectId = useStore(
    useCallback(state => state.lastCopiedClipDestinationProjectId, [])
  );
  const setLastCopiedClipDestinationProjectId = useStore(
    useCallback(state => state.setLastCopiedClipDestinationProjectId, [])
  );
  const lastUsedCombineClipMsGapDuration = useStore(
    useCallback(state => state.lastUsedCombineClipMsGapDuration, [])
  );
  const setLastUsedCombineClipMsGapDuration = useStore(
    useCallback(state => state.setLastUsedCombineClipMsGapDuration, [])
  );
  return useMemo(
    () => ({
      search,
      setSearch,
      previouslySelectedClip,
      setPreviouslySelectedClip,
      selectedClipIds,
      setSelectedClipIds,
      lastMovedClipDestinationProjectId,
      setLastMovedClipDestinationProjectId,
      lastCopiedClipDestinationProjectId,
      setLastCopiedClipDestinationProjectId,
      lastUsedCombineClipMsGapDuration,
      setLastUsedCombineClipMsGapDuration,
    }),
    [
      search,
      setSearch,
      previouslySelectedClip,
      setPreviouslySelectedClip,
      selectedClipIds,
      setSelectedClipIds,
      lastMovedClipDestinationProjectId,
      setLastMovedClipDestinationProjectId,
      lastCopiedClipDestinationProjectId,
      setLastCopiedClipDestinationProjectId,
      lastUsedCombineClipMsGapDuration,
      setLastUsedCombineClipMsGapDuration,
    ]
  );
};
